import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { adminListPosts, adminDeletePost } from "@/lib/blogAdmin.functions";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2 } from "lucide-react";

export const Route = createFileRoute("/tools/blog/")({
  head: () => ({
    meta: [{ title: "Blog posts" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: AdminBlogList,
});

type Row = Awaited<ReturnType<typeof adminListPosts>>[number];

function AdminBlogList() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function load() {
    try {
      setRows(await adminListPosts());
    } catch (e: any) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function onDelete(id: string, title: string) {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await adminDeletePost({ data: { id } });
    load();
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Blog posts</h1>
        <Button onClick={() => navigate({ to: "/tools/blog/new" })}>
          <Plus className="mr-2 h-4 w-4" /> New post
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {!rows ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No posts yet.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="p-3">Title</th>
                <th className="p-3">Slug</th>
                <th className="p-3">Category</th>
                <th className="p-3">Status</th>
                <th className="p-3">Updated</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-medium">{r.title}</td>
                  <td className="p-3 text-muted-foreground">{r.slug}</td>
                  <td className="p-3 text-muted-foreground">{r.category}</td>
                  <td className="p-3">
                    {r.published ? (
                      <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                        Published
                      </span>
                    ) : (
                      <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        Draft
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {new Date(r.updated_at).toLocaleDateString()}
                  </td>
                  <td className="p-3 text-right">
                    <Link
                      to="/tools/blog/$id"
                      params={{ id: r.id }}
                      className="mr-2 inline-flex items-center gap-1 text-brand-text underline underline-offset-2"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Link>
                    <button
                      onClick={() => onDelete(r.id, r.title)}
                      className="inline-flex items-center gap-1 text-destructive hover:underline"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
